/**
 * Phone lines: the inbound numbers this console serves, mirrored from the
 * telephony preset.
 */
import { z } from 'zod';

/** What a row needs to name the line a call came in on. */
export const phoneLineSummarySchema = z.object({
  id: z.string().uuid(),
  /** E.164. */
  number: z.string(),
  /** A friendlier name, when one is set. */
  label: z.string().nullable(),
});
export type PhoneLineSummary = z.infer<typeof phoneLineSummarySchema>;

export const phoneLineSchema = phoneLineSummarySchema.extend({
  /** False once the number has left the preset; its calls keep it. */
  active: z.boolean(),
});
export type PhoneLine = z.infer<typeof phoneLineSchema>;

export const phoneLineListSchema = z.object({ items: z.array(phoneLineSchema) });
export type PhoneLineList = z.infer<typeof phoneLineListSchema>;

/**
 * A greeting is spoken aloud, so the cap is about how long a caller will listen
 * before they can say why they rang, not about storage.
 */
export const GREETING_MAX = 300;

/** The placeholder a greeting may use for the conversation agent's name. */
export const AGENT_PLACEHOLDER = '{agent}';

/**
 * The line's opening line, as the editor shows it.
 *
 * Greetings live on the INBOUND telephony preset, as the number's
 * `sessionOverrides.first_message` (G6 proved the round trip safe). With no
 * override, the voice preset's own first message is what callers hear.
 */
export const lineGreetingSchema = z.object({
  line: phoneLineSummarySchema,
  /** The greeting as written, which may contain `{agent}`. Null: no override. */
  template: z.string().nullable(),
  /** What a caller hears now: the override, or the voice preset's own message. */
  spoken: z.string().nullable(),
  /** The voice preset's own first message, used when there is no override. */
  presetDefault: z.string().nullable(),
  /** The conversation agent's name, for filling `{agent}` in a preview. */
  agentName: z.string().nullable(),
  /** The telephony preset's `updated`: the version a save must not have missed. */
  presetUpdatedAt: z.string().datetime().nullable(),
  /** Who last changed it from this console, and when. */
  updatedBy: z.string().nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type LineGreeting = z.infer<typeof lineGreetingSchema>;

export const lineGreetingUpdateSchema = z.object({
  /** The new greeting. Null or empty removes the override. */
  template: z.string().trim().max(GREETING_MAX).nullable(),
  expectedPresetUpdatedAt: z.string().datetime().nullable(),
});
export type LineGreetingUpdate = z.infer<typeof lineGreetingUpdateSchema>;

/**
 * The greeting as a caller will hear it.
 *
 * With no name to fill in, the placeholder is left as written rather than
 * guessed, so the editor can say so instead of the agent saying "{agent}".
 */
export function fillGreeting(template: string, agentName: string | null): string {
  if (agentName === null || agentName.trim() === '') return template;
  return template.replaceAll(AGENT_PLACEHOLDER, agentName.trim());
}
